
import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Check, Trash2, UserPlus, Clock, User } from 'lucide-react';

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState('');
  const { axiosInstance } = useAuth();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get('/notifications/admin/all');
      setNotifications(response.data || []);
      setDebugInfo(`✅ ${response.data?.length || 0} notifications`);
    } catch (error) {
      setDebugInfo('❌ erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axiosInstance.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch {
      toast.error('Erreur');
    }
  };

  const deleteAll = async () => {
    try {
      await axiosInstance.delete('/notifications/admin/all');
      toast.success('Supprimées');
      fetchNotifications();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'new_user':
        return <UserPlus className="text-blue-600" size={18} />;
      default:
        return <Bell className="text-amber-600" size={18} />;
    }
  };

  const getBadge = (type) => {
    const map = {
      new_user: 'bg-blue-500 text-white',
      message: 'bg-green-500 text-white',
      alert: 'bg-red-500 text-white'
    };
    return (
      <Badge className={`${map[type] || 'bg-gray-500 text-white'} text-xs`}>
        {type}
      </Badge>
    );
  };

  const formatTime = (date) => {
    const diff = Math.floor((new Date() - new Date(date)) / 60000);

    if (diff < 1) return "à l'instant";
    if (diff < 60) return `il y a ${diff} min`;
    if (diff < 1440) return `il y a ${Math.floor(diff / 60)} h`;
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const shortId = (id) => id?.slice(-6);

  if (loading) {
    return (
      <Layout title="Admin Notifications">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Admin Notifications">

      <div className="space-y-5">

        {/* Header compact */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {debugInfo}
          </div>

          <Button
            onClick={deleteAll}
            variant="outline"
            className="border-red-600 text-red-700 text-sm"
          >
            <Trash2 size={16} className="mr-1" />
            Tout supprimer
          </Button>
        </div>

        {/* List */}
        <div className="space-y-3">

          {notifications.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-gray-500 text-sm">
                Aucune notification admin
              </CardContent>
            </Card>
          ) : (

            notifications.map((n) => (
              <Card
                key={n._id}
                className={`transition hover:shadow-md ${
                  !n.read_status ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <CardContent className="p-4">

                  <div className="flex justify-between">

                    {/* LEFT */}
                    <div className="flex gap-3">

                      {/* ICON */}
                      <div className="mt-1">
                        {getIcon(n.type)}
                      </div>

                      {/* CONTENT */}
                      <div className="space-y-1">

                        {/* TOP META */}
                        <div className="flex items-center gap-2 flex-wrap">

                          {getBadge(n.type)}

                          {!n.read_status && (
                            <Badge className="bg-red-500 text-white text-xs">
                              nouveau
                            </Badge>
                          )}

                          {n.user_name && (
                            <span className="flex items-center text-xs text-gray-600 gap-1">
                              <User size={12} />
                              {n.user_name}
                            </span>
                          )}

                          <span className="text-xs text-gray-400">
                            #{shortId(n._id)}
                          </span>

                        </div>

                        {/* MESSAGE */}
                        <p className="text-sm font-medium text-gray-900">
                          {n.message}
                        </p>

                        {/* BOTTOM META */}
                        <div className="flex items-center gap-3 text-xs text-gray-500">

                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {formatTime(n.timestamp)}
                          </span>

                        </div>
                      </div>
                    </div>

                    {/* ACTION */}
                    {!n.read_status && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsRead(n._id)}
                        className="border-green-600 text-green-700 h-8 text-xs"
                      >
                        <Check size={14} className="mr-1" />
                        marquer comme lu
                      </Button>
                    )}
                  </div>

                </CardContent>
              </Card>
            ))

          )}
        </div>
      </div>

    </Layout>
  );
}